import React, { useEffect, useState, useRef, useCallback } from "react";
import Navbar from "./Navbar";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection, getDocs, doc, setDoc, query, where, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// ─── helpers ──────────────────────────────────────────────────────────────────
const FALLBACK_PHOTO = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
const getPhotos = (user) =>
  Array.isArray(user.photos) && user.photos.length ? user.photos : [FALLBACK_PHOTO];

// ─── End of deck ──────────────────────────────────────────────────────────────
function EmptyDeck({ onReset }) {
  return (
    <div className="ub-empty">
      <div className="ub-empty__icon">💍</div>
      <h2 className="ub-empty__title">You've seen everyone!</h2>
      <p className="ub-empty__body">Check back soon — new profiles are added regularly.</p>
      <button className="ub-btn-primary" onClick={onReset}>Start over</button>
    </div>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────
function CardSkeleton() {
  return (
    <div className="ub-card ub-card--skeleton">
      <div className="ub-card__photo-side ub-skeleton-block" />
      <div className="ub-card__detail-side">
        <div className="ub-sk-line ub-sk-line--xl" />
        <div className="ub-sk-line ub-sk-line--md" />
        <div className="ub-sk-line ub-sk-line--sm" />
        <div className="ub-sk-chips">{[1,2,3].map(i => <div key={i} className="ub-sk-chip" />)}</div>
        <div className="ub-sk-chips" style={{ marginTop: ".5rem" }}>{[1,2].map(i => <div key={i} className="ub-sk-chip" />)}</div>
      </div>
    </div>
  );
}

// ─── Match Overlay ────────────────────────────────────────────────────────────
function MatchOverlay({ matchedUser, currentUserPhoto, onClose, onMessage }) {
  return (
    <motion.div
      className="ub-match"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="ub-match__orb ub-match__orb--1" />
      <div className="ub-match__orb ub-match__orb--2" />

      <motion.div
        className="ub-match__inner"
        initial={{ scale: 0.7, y: 60 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 14, stiffness: 180, delay: 0.05 }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }} className="ub-match__eyebrow"
        >
          ✦ A new connection
        </motion.div>

        <motion.h2
          className="ub-match__headline"
          initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35, type: "spring", damping: 10 }}
        >
          It's a Match
        </motion.h2>

        <div className="ub-match__photos">
          <motion.div
            className="ub-match__photo-wrap"
            initial={{ x: -120, opacity: 0, rotate: -12 }}
            animate={{ x: 0, opacity: 1, rotate: -6 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
          >
            <img src={currentUserPhoto || FALLBACK_PHOTO} alt="You" className="ub-match__photo" />
          </motion.div>

          <motion.div
            className="ub-match__heart"
            initial={{ scale: 0 }} animate={{ scale: [0, 1.3, 1] }}
            transition={{ delay: 0.55, duration: 0.5 }}
          >♥</motion.div>

          <motion.div
            className="ub-match__photo-wrap"
            initial={{ x: 120, opacity: 0, rotate: 12 }}
            animate={{ x: 0, opacity: 1, rotate: 6 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
          >
            <img src={getPhotos(matchedUser)[0]} alt={matchedUser.name} className="ub-match__photo" />
          </motion.div>
        </div>

        <motion.p
          className="ub-match__sub"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          You and <strong>{matchedUser.name}</strong> expressed mutual interest.<br />
          Visit your matches to connect.
        </motion.p>

        <motion.div
          className="ub-match__actions"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <button className="ub-btn-primary" onClick={onMessage}>View matches →</button>
          <button className="ub-match__skip" onClick={onClose}>Keep browsing</button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

// ─── Swipe Card ───────────────────────────────────────────────────────────────
function SwipeCard({ user, onLike, onPass, isTop }) {
  const photos = getPhotos(user);
  const x = useMotionValue(0);
  const rotate     = useTransform(x, [-200, 200], [-18, 18]);
  const likeOpacity  = useTransform(x, [20, 100],  [0, 1]);
  const passOpacity  = useTransform(x, [-100, -20], [1, 0]);
  const cardScale  = useTransform(x, [-200, 0, 200], [0.95, 1, 0.95]);

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 120)       onLike();
    else if (info.offset.x < -120) onPass();
  };

  const DetailChips = ({ label, items }) => {
    if (!items?.length) return null;
    return (
      <div className="ub-detail-group">
        <span className="ub-detail-label">{label}</span>
        <div className="ub-detail-chips">
          {items.slice(0, 4).map((item, i) => <span key={i} className="ub-chip">{item}</span>)}
          {items.length > 4 && <span className="ub-chip ub-chip--more">+{items.length - 4}</span>}
        </div>
      </div>
    );
  };

  const InfoRow = ({ icon, value }) => {
    if (!value) return null;
    return (
      <span className="ub-info-row">
        <span className="ub-info-row__icon">{icon}</span>
        {value}
      </span>
    );
  };

  return (
    <motion.div
      className={`ub-card ${isTop ? "ub-card--top" : "ub-card--behind"}`}
      style={{ x, rotate, scale: isTop ? cardScale : 0.97 }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragEnd={isTop ? handleDragEnd : undefined}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: isTop ? 1 : 0.97 }}
      exit={{ opacity: 0, x: 0, scale: 0.8, transition: { duration: 0.2 } }}
      whileDrag={{ cursor: "grabbing" }}
    >
      {isTop && (
        <>
          <motion.div className="ub-swipe-hint ub-swipe-hint--like" style={{ opacity: likeOpacity }}>♥ Like</motion.div>
          <motion.div className="ub-swipe-hint ub-swipe-hint--pass" style={{ opacity: passOpacity }}>✕ Pass</motion.div>
        </>
      )}

      {/* photo side */}
      <div className="ub-card__photo-side">
        <Swiper modules={[Navigation, Pagination]} navigation pagination={{ clickable: true }} className="ub-swiper" onClick={e => e.stopPropagation()}>
          {photos.map((url, i) => (
            <SwiperSlide key={i}>
              <img src={url} alt={`${user.name} ${i + 1}`} className="ub-card__photo" loading="lazy" />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="ub-card__photo-grad" />
        <div className="ub-card__age">{user.age}</div>
      </div>

      {/* detail side */}
      <div className="ub-card__detail-side">
        <div className="ub-card__detail-scroll">
          <div className="ub-card__identity">
            <h2 className="ub-card__name">{user.name}</h2>
            <div className="ub-card__meta">
              <InfoRow icon="📍" value={user.location} />
              <InfoRow icon="💼" value={user.occupation} />
              <InfoRow icon="🎓" value={user.educationLevel} />
            </div>
          </div>

          <div className="ub-card__divider" />

          <DetailChips label="Hobbies"     items={user.hobbies} />
          <DetailChips label="Personality" items={user.personalityTraits} />
          <DetailChips label="Looking for" items={user.nonNegotiableQualities} />

          {(user.children || user.finances || user.householdDuties) && (
            <div className="ub-detail-group">
              <span className="ub-detail-label">Lifestyle</span>
              <div className="ub-lifestyle-facts">
                {user.children       && <div className="ub-fact"><span className="ub-fact__key">Children</span><span className="ub-fact__val">{user.children}</span></div>}
                {user.finances       && <div className="ub-fact"><span className="ub-fact__key">Finances</span><span className="ub-fact__val">{user.finances}</span></div>}
                {user.householdDuties && <div className="ub-fact"><span className="ub-fact__key">Household</span><span className="ub-fact__val">{user.householdDuties}</span></div>}
              </div>
            </div>
          )}

          <DetailChips label="Goals" items={user.longTermGoals} />
        </div>

        <div className="ub-card__actions">
          <button className="ub-action-btn ub-action-btn--pass" onClick={onPass} aria-label="Pass">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <div className="ub-action-hint">← swipe or tap →</div>
          <button className="ub-action-btn ub-action-btn--like" onClick={onLike} aria-label="Like">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── main ──────────────────────────────────────────────────────────────────────
export default function Users() {
  const [allUsers,    setAllUsers]    = useState([]);
  const [seenIds,     setSeenIds]     = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [matchedUser, setMatchedUser] = useState(null);
  const [loading,     setLoading]     = useState(true);

  const auth = useRef(getAuth()).current;
  const navigate = (path) => window.location.href = path;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/signin"); return; }
      setCurrentUser(user);
      try {
        const [usersSnap, seenSnap] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(query(collection(db, "interactions"), where("fromUserId", "==", user.uid))),
        ]);
        setAllUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.id !== user.uid));
        setSeenIds(seenSnap.docs.map(d => d.data().toUserId));
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const handleInteraction = useCallback(async (userId, type) => {
    if (!currentUser) return;
    setSeenIds(prev => [...prev, userId]);
    await setDoc(doc(db, "interactions", `${currentUser.uid}_${userId}`), {
      fromUserId: currentUser.uid, toUserId: userId, type, createdAt: serverTimestamp(),
    });
    if (type === "like") {
      const q = query(
        collection(db, "interactions"),
        where("fromUserId", "==", userId),
        where("toUserId",   "==", currentUser.uid),
        where("type",       "==", "like")
      );
      const snap = await getDocs(q);
      if (!snap.empty) setMatchedUser(allUsers.find(u => u.id === userId) || null);
    }
  }, [currentUser, allUsers]);

  const visibleUsers = allUsers.filter(u => !seenIds.includes(u.id));
  const topTwo = visibleUsers.slice(0, 2);

  return (
    <div className="ub-root">
      <Navbar />

      <AnimatePresence>
        {matchedUser && (
          <MatchOverlay
            matchedUser={matchedUser}
            currentUserPhoto={currentUser?.photoURL}
            onClose={() => setMatchedUser(null)}
            onMessage={() => navigate("/matches")}
          />
        )}
      </AnimatePresence>

      <div className="ub-deck-area">
        {loading ? (
          <CardSkeleton />
        ) : visibleUsers.length === 0 ? (
          <EmptyDeck onReset={() => setSeenIds([])} />
        ) : (
          <div className="ub-deck">
            <AnimatePresence mode="popLayout">
              {[...topTwo].reverse().map((user, idx) => {
                const isTop = idx === topTwo.length - 1;
                return (
                  <SwipeCard
                    key={user.id} user={user} isTop={isTop}
                    onLike={() => handleInteraction(user.id, "like")}
                    onPass={() => handleInteraction(user.id, "pass")}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {!loading && visibleUsers.length > 0 && (
          <div className="ub-count">
            {visibleUsers.length} profile{visibleUsers.length !== 1 ? "s" : ""} left
          </div>
        )}
      </div>
    </div>
  );
}